import { useState, useMemo, useEffect } from 'react';
import { X, Copy, Check, ChevronDown, ChevronRight, Terminal, GitBranch, Code, FileJson, Download, Loader2 } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getTimingBreakdown, getStatusColor, generateCurl, getInitiatorChain, getFileName } from '@har-insight/parser';
import type { InitiatorChainItem } from '@har-insight/parser';
import { cn, copyToClipboard, sanitizeHtml } from '@/lib/utils';
import { highlight, detectLanguage, safeDecodeURIComponent, type Language } from '@/lib/syntax-highlight';

type Tab = 'headers' | 'payload' | 'response' | 'cookies' | 'timing' | 'initiator';
type CopyState = 'idle' | 'url' | 'curl';

export function RequestDetail() {
  const { selectedEntry, setSidebarOpen, har } = useHARStore();
  const [activeTab, setActiveTab] = useState<Tab>('headers');
  const [copyState, setCopyState] = useState<CopyState>('idle');

  if (!selectedEntry) return null;

  const { request, response, timings } = selectedEntry;
  const statusColor = getStatusColor(response.status);
  const timingBreakdown = getTimingBreakdown(timings);
  const initiatorChain = har ? getInitiatorChain(selectedEntry, har.log.entries) : [];

  const handleCopyUrl = async () => {
    await copyToClipboard(request.url);
    setCopyState('url');
    setTimeout(() => setCopyState('idle'), 2000);
  };

  const handleCopyCurl = async () => {
    const curl = generateCurl(selectedEntry);
    await copyToClipboard(curl);
    setCopyState('curl');
    setTimeout(() => setCopyState('idle'), 2000);
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'headers', label: 'Headers' },
    { key: 'payload', label: 'Payload' },
    { key: 'response', label: 'Response' },
    { key: 'cookies', label: 'Cookies' },
    { key: 'timing', label: 'Timing' },
    { key: 'initiator', label: 'Initiator' },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-800 rounded text-xs font-medium">
                {request.method}
              </span>
              <span className={cn('font-mono text-sm', statusColor)}>
                {response.status} {response.statusText}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-sm text-slate-700 dark:text-slate-300 truncate flex-1" title={request.url}>
                {request.url}
              </p>
              <button
                onClick={handleCopyUrl}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                title="Copy URL"
              >
                {copyState === 'url' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Copy className="w-4 h-4 text-slate-500" />
                )}
              </button>
              <button
                onClick={handleCopyCurl}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                title="Copy as cURL"
              >
                {copyState === 'curl' ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Terminal className="w-4 h-4 text-slate-500" />
                )}
              </button>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Quick stats */}
        <div className="flex gap-4 mt-4 text-xs text-slate-400 dark:text-slate-500">
          <span>Size: {formatBytes(response.content.size || 0)}</span>
          <span>Time: {formatTime(selectedEntry.time || 0)}</span>
          <span>Type: {response.content.mimeType}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 flex overflow-x-auto scrollbar-hide border-b border-slate-200 dark:border-slate-800">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex-shrink-0 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap',
              activeTab === tab.key
                ? 'text-brand-600 dark:text-brand-400 border-b-2 border-brand-500 dark:border-brand-400'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0 p-4">
        {activeTab === 'headers' && (
          <div className="flex-1 overflow-auto">
            <HeadersTab
              requestHeaders={request.headers}
              responseHeaders={response.headers}
            />
          </div>
        )}
        {activeTab === 'payload' && (
          <div className="flex-1 overflow-auto">
            <PayloadTab postData={request.postData} queryString={request.queryString} />
          </div>
        )}
        {activeTab === 'response' && (
          <ResponseTab content={response.content} />
        )}
        {activeTab === 'cookies' && (
          <div className="flex-1 overflow-auto">
            <CookiesTab
              requestCookies={request.cookies}
              responseCookies={response.cookies}
            />
          </div>
        )}
        {activeTab === 'timing' && (
          <div className="flex-1 overflow-auto">
            <TimingTab breakdown={timingBreakdown} total={selectedEntry.time || 0} />
          </div>
        )}
        {activeTab === 'initiator' && (
          <div className="flex-1 overflow-auto">
            <InitiatorTab
              entry={selectedEntry}
              chain={initiatorChain}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Sub-components for each tab

function HeadersTab({
  requestHeaders,
  responseHeaders
}: {
  requestHeaders?: Array<{ name: string; value: string }>;
  responseHeaders?: Array<{ name: string; value: string }>;
}) {
  // Ensure arrays are defined
  const safeRequestHeaders = requestHeaders || [];
  const safeResponseHeaders = responseHeaders || [];

  return (
    <div className="space-y-6">
      <HeaderSection title="Request Headers" headers={safeRequestHeaders} />
      <HeaderSection title="Response Headers" headers={safeResponseHeaders} />
    </div>
  );
}

function HeaderSection({
  title,
  headers
}: {
  title: string;
  headers: Array<{ name: string; value: string }>;
}) {
  const [expanded, setExpanded] = useState(true);
  
  // Ensure headers is an array
  const safeHeaders = headers || [];

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {title} ({safeHeaders.length})
      </button>
      {expanded && (
        <div className="space-y-1 ml-6">
          {safeHeaders.length === 0 ? (
            <p className="text-xs text-slate-400 italic">No headers</p>
          ) : (
            safeHeaders.map((header, i) => (
              <div key={i} className="text-xs">
                <span className="text-brand-600 dark:text-brand-400">{header.name}:</span>{' '}
                <span className="text-slate-600 dark:text-slate-400 break-all">{header.value}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const MAX_PAYLOAD_SIZE = 500 * 1024; // 500KB threshold

function PayloadTab({
  postData,
  queryString
}: {
  postData?: { mimeType: string; text?: string; params?: Array<{ name: string; value?: string }> };
  queryString: Array<{ name: string; value: string }>;
}) {
  const [showFullContent, setShowFullContent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [displayContent, setDisplayContent] = useState<string>('');

  // Get language display name for post data
  const postDataLanguage = useMemo(() => {
    if (!postData?.text) return null;
    const lang = detectLanguage(postData.mimeType);
    const labels: Record<Language, string> = {
      json: 'JSON',
      xml: 'XML',
      html: 'HTML',
      css: 'CSS',
      javascript: 'JavaScript',
      text: 'Plain Text',
    };
    return labels[lang] || 'Text';
  }, [postData]);

  // Check if content is large
  const contentSize = postData?.text?.length || 0;
  const isLargeContent = contentSize > MAX_PAYLOAD_SIZE;

  // Prepare content asynchronously for large payloads
  useEffect(() => {
    if (!postData?.text) {
      setDisplayContent('');
      return;
    }

    // For small content, process immediately
    if (!isLargeContent) {
      const language = detectLanguage(postData.mimeType);
      let formatted = postData.text;
      
      if (language === 'json') {
        try {
          formatted = JSON.stringify(JSON.parse(postData.text), null, 2);
        } catch {
          // Keep original if not valid JSON
        }
      }
      
      setDisplayContent(highlight(formatted, language));
      return;
    }

    // For large content, show preview or full content based on state
    if (!showFullContent) {
      // Show truncated preview without highlighting for performance
      const PREVIEW_SIZE = 50000;
      const preview = postData.text.substring(0, PREVIEW_SIZE);
      setDisplayContent(preview + '\n\n... [Content truncated - ' + formatBytes(contentSize) + ' total]');
    } else {
      // Load full content asynchronously with progress
      setIsLoading(true);
      setProgress(0);
      
      // Capture values before async operation
      const mimeType = postData.mimeType || 'text/plain';
      const text = postData.text;
      
      // Use setTimeout to allow UI to update
      setTimeout(() => {
        const language = detectLanguage(mimeType);
        let formatted = text;
        
        if (language === 'json') {
          try {
            formatted = JSON.stringify(JSON.parse(text), null, 2);
          } catch {
            // Keep original if not valid JSON
          }
        }
        
        // Process in chunks for progress indication
        const totalLength = formatted.length;
        const chunkSize = Math.ceil(totalLength / 10);
        let processedLength = 0;
        
        const processChunk = () => {
          processedLength += chunkSize;
          const currentProgress = Math.min((processedLength / totalLength) * 100, 100);
          setProgress(currentProgress);
          
          if (processedLength < totalLength) {
            requestAnimationFrame(processChunk);
          } else {
            // For very large content (>1MB), skip highlighting to prevent UI freeze
            if (contentSize > 1024 * 1024) {
              setDisplayContent(formatted);
            } else {
              setDisplayContent(highlight(formatted, language));
            }
            setIsLoading(false);
          }
        };
        
        processChunk();
      }, 100);
    }
  }, [postData, showFullContent, isLargeContent, contentSize]);

  // Handle download
  const handleDownload = () => {
    if (!postData?.text) return;
    const blob = new Blob([postData.text], { type: postData.mimeType || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'request-body' + (postData.mimeType?.includes('json') ? '.json' : '.txt');
    a.click();
    URL.revokeObjectURL(url);
  };

  // Ensure arrays are defined
  const safeQueryString = queryString || [];
  const hasPostData = postData && (postData.text || (postData.params && postData.params.length > 0));

  return (
    <div className="space-y-6">
      {safeQueryString.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Query Parameters ({safeQueryString.length})
          </h3>
          <div className="space-y-1.5 bg-slate-100 dark:bg-slate-800/50 p-3 rounded">
            {safeQueryString.map((param, i) => (
              <div key={i} className="text-xs flex items-start gap-2">
                <span className="text-brand-600 dark:text-brand-400 font-medium whitespace-nowrap">{param.name}:</span>
                <span className="text-slate-600 dark:text-slate-400 break-all font-mono">{safeDecodeURIComponent(param.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasPostData && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Request Body
            </h3>
            <div className="flex items-center gap-2">
              {postDataLanguage && (
                <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                  {postDataLanguage}
                </span>
              )}
              {postData?.text && (
                <span className="text-xs text-slate-400">
                  {formatBytes(contentSize)}
                </span>
              )}
            </div>
          </div>
          
          {postData?.text && (
            <>
              {isLargeContent && !showFullContent && (
                <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                    <span className="font-semibold">Large payload detected:</span> {formatBytes(contentSize)}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mb-3">
                    Showing preview of first 50KB. Loading full content may freeze the browser temporarily.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFullContent(true)}
                      className="px-3 py-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
                    >
                      Load Full Content
                    </button>
                    <button
                      onClick={handleDownload}
                      className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Download
                    </button>
                  </div>
                </div>
              )}
              
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12 bg-slate-100 dark:bg-slate-800/50 rounded">
                  <div className="w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-3">
                    <div 
                      className="h-full bg-brand-500 transition-all duration-150 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading content... {Math.round(progress)}%</span>
                  </div>
                </div>
              ) : (
                <pre
                  className="text-xs bg-slate-100 dark:bg-slate-800/50 p-3 rounded overflow-auto max-h-[400px] font-mono whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayContent) }}
                />
              )}
            </>
          )}
          
          {postData?.params && postData.params.length > 0 && (
            <div className="space-y-1.5 bg-slate-100 dark:bg-slate-800/50 p-3 rounded">
              {postData.params.map((param, i) => (
                <div key={i} className="text-xs flex items-start gap-2">
                  <span className="text-brand-600 dark:text-brand-400 font-medium whitespace-nowrap">{param.name}:</span>
                  <span className="text-slate-600 dark:text-slate-400 break-all font-mono">{param.value || '(file)'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!hasPostData && safeQueryString.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-slate-500">No payload data</p>
        </div>
      )}
    </div>
  );
}

function ResponseTab({
  content
}: {
  content: { size: number; mimeType: string; text?: string; encoding?: string };
}) {
  const [search, setSearch] = useState('');
  const [wrapEnabled, setWrapEnabled] = useState(true);
  const [showFullContent, setShowFullContent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [displayContent, setDisplayContent] = useState<string>('');
  
  const MAX_RESPONSE_SIZE = 500 * 1024; // 500KB threshold
  const PREVIEW_SIZE = 50000; // 50KB preview

  const detectedLanguage = useMemo(() => {
    return detectLanguage(content.mimeType);
  }, [content.mimeType]);

  const isLarge = content.size > MAX_RESPONSE_SIZE;
  const isBinary = !content.text;
  const isImage = content.mimeType?.startsWith('image/');

  // Language icon
  const LanguageIcon = useMemo(() => {
    if (detectedLanguage === 'json') return FileJson;
    if (['javascript', 'html', 'css', 'xml'].includes(detectedLanguage)) return Code;
    return Code;
  }, [detectedLanguage]);

  // Language label
  const languageLabel = useMemo(() => {
    const labels: Record<Language, string> = {
      json: 'JSON',
      xml: 'XML',
      html: 'HTML',
      css: 'CSS',
      javascript: 'JavaScript',
      text: 'Plain Text',
    };
    return labels[detectedLanguage] || 'Binary';
  }, [detectedLanguage]);

  // Load content with progress
  useEffect(() => {
    if (!content.text) {
      setDisplayContent('');
      return;
    }

    if (!isLarge) {
      // Small content - load immediately
      const language = detectLanguage(content.mimeType);
      let formatted = content.text;
      
      if (language === 'json') {
        try {
          formatted = JSON.stringify(JSON.parse(content.text), null, 2);
        } catch {
          // Keep original if not valid JSON
        }
      }
      
      setDisplayContent(highlight(formatted, language));
      return;
    }

    // Large content
    if (!showFullContent) {
      // Show preview
      const preview = content.text.substring(0, PREVIEW_SIZE);
      setDisplayContent(
        preview + 
        `\n\n<div class="text-slate-500 italic">... [${formatBytes(content.size - PREVIEW_SIZE)} more - Click "Load All" to view full content]</div>`
      );
    } else {
      // Load full content with progress
      setIsLoading(true);
      setProgress(0);
      
      const text = content.text;
      const mimeType = content.mimeType;
      
      setTimeout(() => {
        const language = detectLanguage(mimeType);
        let formatted = text;
        
        if (language === 'json') {
          try {
            formatted = JSON.stringify(JSON.parse(text), null, 2);
          } catch {
            // Keep original
          }
        }
        
        // Simulate chunked loading for progress
        let currentProgress = 0;
        const increment = 10;
        
        const updateProgress = () => {
          currentProgress += increment;
          const progressPercent = Math.min((currentProgress / 100) * 100, 100);
          setProgress(progressPercent);
          
          if (currentProgress < 100) {
            requestAnimationFrame(updateProgress);
          } else {
            // For very large content (>1MB), skip highlighting
            if (content.size > 1024 * 1024) {
              setDisplayContent(formatted);
            } else {
              setDisplayContent(highlight(formatted, language));
            }
            setIsLoading(false);
          }
        };
        
        updateProgress();
      }, 100);
    }
  }, [content, isLarge, showFullContent]);

  // Handle download
  const handleDownload = () => {
    if (!content.text) return;
    const blob = new Blob([content.text], { type: content.mimeType || 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response' + (content.mimeType?.includes('json') ? '.json' : '.txt');
    a.click();
    URL.revokeObjectURL(url);
  };

  // Compute search highlighting
  const { finalContent, matchCount } = useMemo(() => {
    if (!search.trim() || !displayContent) {
      return { finalContent: displayContent, matchCount: 0 };
    }

    try {
      const escapedSearch = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedSearch})`, 'gi');
      const matches = displayContent.match(regex);
      
      if (!matches || matches.length === 0) {
        return { finalContent: displayContent, matchCount: 0 };
      }

      const searchHighlighted = displayContent.replace(
        new RegExp(`(${escapedSearch})(?![^<>]*>)`, 'gi'),
        '<mark class="bg-yellow-300 dark:bg-yellow-500/50 text-slate-900 dark:text-slate-100">$1</mark>'
      );
      
      return { finalContent: searchHighlighted, matchCount: matches.length };
    } catch {
      return { finalContent: displayContent, matchCount: 0 };
    }
  }, [search, displayContent]);

  if (isBinary) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="w-16 h-16 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
          <Code className="w-8 h-8 text-slate-400" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Binary Response
          </p>
          <p className="text-xs text-slate-500">
            {content.mimeType} • {formatBytes(content.size)}
          </p>
        </div>
        {isImage && (
          <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <p className="text-xs text-slate-500 text-center">
              Image preview not available in HAR view
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-2">
      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search in response..."
            className="input w-full text-xs py-1.5"
          />
          {search && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">
              {matchCount} match{matchCount !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
        
        {/* Wrap toggle */}
        <button
          onClick={() => setWrapEnabled(!wrapEnabled)}
          className={cn(
            "px-2 py-1.5 text-xs rounded transition-colors",
            wrapEnabled 
              ? "bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400" 
              : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          )}
          title={wrapEnabled ? "Disable word wrap" : "Enable word wrap"}
        >
          Wrap
        </button>
        
        <button
          onClick={() => {
            // Copy raw text without HTML
            if (content.text) {
              copyToClipboard(content.text);
            }
          }}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-500 transition-colors"
          title="Copy response body"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        
        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs">
          <LanguageIcon className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-600 dark:text-slate-400">{languageLabel}</span>
        </div>
        
        <span className="text-xs text-slate-500">{formatBytes(content.size)}</span>
      </div>

      {/* Large content warning */}
      {isLarge && !showFullContent && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
            <span className="font-semibold">Large response:</span> {formatBytes(content.size)}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-500 mb-3">
            Showing preview of first {formatBytes(PREVIEW_SIZE)}. Loading full content may take a moment.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFullContent(true)}
              className="px-3 py-1.5 text-xs font-medium bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
            >
              Load All
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
            >
              <Download className="w-3 h-3" />
              Download
            </button>
          </div>
        </div>
      )}

      {/* Loading state with progress */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-8 bg-slate-100 dark:bg-slate-800/50 rounded">
          <div className="w-48 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full bg-brand-500 transition-all duration-150 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Rendering... {Math.round(progress)}%</span>
          </div>
        </div>
      )}

      {/* Content display */}
      {!isLoading && (
        <pre
          className={cn(
            "flex-1 text-xs bg-slate-100 dark:bg-slate-800/50 p-3 rounded overflow-auto min-h-0 font-mono",
            wrapEnabled ? "whitespace-pre-wrap break-words" : "whitespace-pre"
          )}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(finalContent) }}
        />
      )}
    </div>
  );
}

function CookiesTab({
  requestCookies,
  responseCookies
}: {
  requestCookies?: Array<{ name: string; value: string }>;
  responseCookies?: Array<{ name: string; value: string; path?: string; domain?: string; expires?: string; httpOnly?: boolean; secure?: boolean }>;
}) {
  // Ensure arrays are defined
  const safeRequestCookies = requestCookies || [];
  const safeResponseCookies = responseCookies || [];

  return (
    <div className="space-y-6">
      {safeRequestCookies.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Request Cookies ({safeRequestCookies.length})
          </h3>
          <div className="space-y-1 bg-slate-100 dark:bg-slate-800/50 p-3 rounded">
            {safeRequestCookies.map((cookie, i) => (
              <div key={i} className="text-xs flex items-start gap-2">
                <span className="text-brand-600 dark:text-brand-400 font-medium whitespace-nowrap">{cookie.name}:</span>
                <span className="text-slate-600 dark:text-slate-400 break-all font-mono">{cookie.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {safeResponseCookies.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Response Cookies ({safeResponseCookies.length})
          </h3>
          <div className="space-y-2">
            {safeResponseCookies.map((cookie, i) => (
              <div key={i} className="text-xs bg-slate-100 dark:bg-slate-800/50 p-2 rounded">
                <div className="flex items-start gap-2">
                  <span className="text-brand-600 dark:text-brand-400 font-medium whitespace-nowrap">{cookie.name}:</span>
                  <span className="text-slate-600 dark:text-slate-400 break-all font-mono">{cookie.value}</span>
                </div>
                <div className="flex gap-2 mt-1.5 text-slate-400 dark:text-slate-600 flex-wrap">
                  {cookie.httpOnly && <span className="badge badge-info">HttpOnly</span>}
                  {cookie.secure && <span className="badge badge-success">Secure</span>}
                  {cookie.path && <span className="text-[10px]">Path: {cookie.path}</span>}
                  {(cookie as unknown as { sameSite?: string }).sameSite && (
                    <span className="text-[10px]">SameSite: {(cookie as unknown as { sameSite?: string }).sameSite}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {safeRequestCookies.length === 0 && safeResponseCookies.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-slate-400 dark:text-slate-500">No cookies</p>
        </div>
      )}
    </div>
  );
}

function TimingTab({
  breakdown,
  total
}: {
  breakdown: Array<{ name: string; duration: number; offset: number; color: string }>;
  total: number;
}) {
  // Ensure breakdown is an array
  const safeBreakdown = breakdown || [];
  const safeTotal = total || 0;

  return (
    <div className="space-y-4">
      {safeBreakdown.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500">No timing data available</p>
      ) : (
        <div className="space-y-2">
          {safeBreakdown.map((phase, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: phase.color }}
              />
              <span className="text-sm text-slate-500 dark:text-slate-400 w-20">{phase.name}</span>
              <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-800 rounded overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    backgroundColor: phase.color,
                    width: `${safeTotal > 0 ? (phase.duration / safeTotal) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="text-xs text-slate-500 w-16 text-right font-mono">
                {formatTime(phase.duration)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500 dark:text-slate-400">Total</span>
          <span className="font-mono text-slate-800 dark:text-slate-200">{formatTime(safeTotal)}</span>
        </div>
      </div>
    </div>
  );
}

function InitiatorTab({
  entry,
  chain,
}: {
  entry: { _initiator?: { type: string; url?: string } };
  chain: InitiatorChainItem[];
}) {
  const initiator = entry._initiator;

  if (!initiator) {
    return (
      <div className="text-sm text-slate-400 dark:text-slate-500">
        No initiator information available
      </div>
    );
  }

  const typeColors: Record<string, string> = {
    parser: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    script: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    preflight: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    other: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
  };

  return (
    <div className="space-y-4">
      {/* Initiator type */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Initiator Type</h3>
        <span className={cn('px-2 py-1 rounded text-xs font-medium', typeColors[initiator.type] || typeColors.other)}>
          {initiator.type}
        </span>
      </div>

      {/* Initiator chain */}
      {chain.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            <GitBranch className="w-4 h-4 inline mr-1" />
            Initiator Chain
          </h3>
          <div className="space-y-1">
            {chain.map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs"
                style={{ paddingLeft: `${i * 12}px` }}
              >
                <span className="text-slate-400 dark:text-slate-600 mt-0.5">
                  {i === 0 ? '├' : '└'}─
                </span>
                <div className="flex-1 min-w-0">
                  <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium mr-2', typeColors[item.type] || typeColors.other)}>
                    {item.type}
                  </span>
                  {item.url && (
                    <span className="text-slate-600 dark:text-slate-400 break-all" title={item.url}>
                      {getFileName(item.url)}
                      {item.lineNumber !== undefined && (
                        <span className="text-slate-400 dark:text-slate-600">
                          :{item.lineNumber}
                          {item.columnNumber !== undefined && `:${item.columnNumber}`}
                        </span>
                      )}
                    </span>
                  )}
                  {item.functionName && (
                    <span className="text-brand-600 dark:text-brand-400 ml-1">
                      {item.functionName}()
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw initiator URL */}
      {initiator.url && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Source URL</h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 break-all bg-slate-100 dark:bg-slate-800/50 p-2 rounded">
            {initiator.url}
          </p>
        </div>
      )}
    </div>
  );
}
